"use client";

import NextImage from "next/image";

/**
 * StappenPlanBlock — 5-stappen proces in 4 visuele varianten.
 *
 * Port van Claude Design "stappen-blok/Stappen Blok.html". Varianten:
 *   - timeline   : 5 horizontale cirkels met dashed connector + tijd-pill
 *   - zigzag     : kaarten afwisselend links/rechts met centrale cirkel
 *   - display    : dark background met grote Fraunces-cijfers (statement)
 *   - accordion  : klikbare lijst, accordion-stijl met time-badge rechts
 *
 * Kleuren volgen het thema via CSS vars — elk pagina-thema werkt automatisch.
 */

import { useState } from "react";

export type StappenPlanVariant =
  | "timeline"
  | "zigzag"
  | "display"
  | "accordion"
  | "photo-cards"
  | "photo-split"
  | "icon-circles"
  | "photo-zigzag";

export interface StappenPlanStep {
  n: string;
  title: string;
  body?: string;
  time?: string;
  /** Public URL van foto voor de visuele varianten. */
  photo_src?: string;
  /** Naam van een ingebouwde SVG illustratie (voor icon-circles): brochure, chat, plan, document, key. */
  illustration?: "brochure" | "chat" | "plan" | "document" | "key";
}

export interface StappenPlanContent {
  eyebrow?: string;
  headline?: string;
  body?: string;
  phone?: string;
  cta_text?: string;
  cta_href?: string;
  cta_subnote?: string;
  steps?: StappenPlanStep[];
}

interface Props {
  content: StappenPlanContent;
  variant?: StappenPlanVariant;
}

const T = {
  bg: "var(--cream-dark, #F1F3F6)",
  card: "var(--cream, #FFFFFF)",
  sand: "var(--cream, #F7F2EA)",
  accent: "var(--sage, #1E3A5F)",
  accentSoft: "var(--sage-light, #D3DDE8)",
  ink: "var(--ink, #141C28)",
  muted: "var(--stone, #5A6578)",
  border: "rgba(20,28,40,0.10)",
  cta: "var(--terracotta, #FF6B1A)",
};

const DEFAULT_STEPS: StappenPlanStep[] = [
  { n: "01", title: "Gratis brochure aanvragen", time: "30 sec", body: "Vul in 30 seconden het formulier in en ontvang onze uitgebreide brochure met mogelijkheden en prijsindicaties." },
  { n: "02", title: "Persoonlijk adviesgesprek", time: "Binnen 24u", body: "Een van onze adviseurs neemt contact op om uw situatie door te nemen en vrijblijvend mee te denken." },
  { n: "03", title: "Gratis 3D-ontwerp & inmeten", time: "Gratis 3D", body: "We komen gratis langs om de badkamer op te meten en maken een 3D-ontwerp op maat — zo ziet u precies hoe uw nieuwe badkamer wordt, zonder verplichtingen." },
  { n: "04", title: "Vaste offerte ontvangen", time: "Geen meerwerk", body: "U ontvangt een heldere, alles-inclusieve offerte. Geen meerwerk, geen verrassingen — u weet exact wat u krijgt en wat u betaalt." },
  { n: "05", title: "Vakkundige montage & oplevering", time: "1–5 dagen", body: "Onze eigen monteurs realiseren uw nieuwe badkamer — van bad naar douche in 1 dag, volledige renovatie in 2 tot 5 dagen. Strak opgeleverd, alles werkend." },
];

const DEFAULTS: Record<StappenPlanVariant, Partial<StappenPlanContent>> = {
  timeline: {
    eyebrow: "Zo werkt het",
    headline: "Van aanvraag tot nieuwe badkamer in 5 stappen.",
    cta_text: "Vraag gratis brochure aan",
    cta_subnote: "Geen verplichtingen · reactie binnen 24 uur",
  },
  zigzag: {
    eyebrow: "Het traject — stap voor stap",
    headline: "Zó helder werken wij.",
    cta_text: "Vraag gratis brochure aan",
  },
  display: {
    eyebrow: "Zo werkt het",
    headline: "Vijf stappen.",
    cta_text: "Vraag gratis brochure aan",
  },
  accordion: {
    eyebrow: "Proces in 5 stappen",
    headline: "Geen stress, geen verrassingen.",
    body: "Van eerste aanvraag tot oplevering: u weet elk moment wat er gebeurt. Duidelijke offerte, vaste vakmensen, geen meerwerk.",
    cta_text: "Vraag gratis brochure aan",
  },
  "photo-cards": {
    eyebrow: "Zo werkt het",
    headline: "Nieuwe badkamer in 5 stappen.",
    cta_text: "Vraag gratis brochure aan",
  },
  "photo-split": {
    eyebrow: "Het traject",
    headline: "Vijf stappen. Eén aanspreekpunt.",
    cta_text: "Vraag gratis brochure aan",
  },
  "icon-circles": {
    eyebrow: "Zo werkt het",
    headline: "Helder. Eerlijk. Ontzorgend.",
    cta_text: "Vraag gratis brochure aan",
  },
  "photo-zigzag": {
    eyebrow: "5 stappen",
    headline: "Van brochure tot nieuwe badkamer.",
    cta_text: "Vraag gratis brochure aan",
  },
};

const DEFAULT_VISUAL_STEPS: StappenPlanStep[] = DEFAULT_STEPS.map((s, i) => ({
  ...s,
  illustration: (["brochure", "chat", "plan", "document", "key"] as const)[i] ?? "brochure",
}));

// Ingebouwde SVG illustraties — matchen het ontwerp 1-op-1.
function Illustration({ name, color, accent }: { name: StappenPlanStep["illustration"]; color: string; accent: string }) {
  switch (name) {
    case "brochure":
      return (
        <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="20" y="38" width="80" height="56" rx="4" fill={color} opacity="0.18" />
          <rect x="30" y="30" width="52" height="68" rx="3" fill="white" stroke={color} strokeWidth="2" />
          <rect x="36" y="38" width="40" height="4" rx="1" fill={color} opacity="0.6" />
          <rect x="36" y="46" width="32" height="3" rx="1" fill={color} opacity="0.3" />
          <rect x="36" y="52" width="38" height="3" rx="1" fill={color} opacity="0.3" />
          <rect x="36" y="60" width="40" height="26" rx="2" fill={accent} opacity="0.25" />
          <circle cx="44" cy="70" r="3" fill={accent} opacity="0.5" />
          <path d="M38 82 L48 74 L58 80 L74 70 L74 86 L38 86 Z" fill={accent} opacity="0.5" />
          <path d="M20 42 L60 68 L100 42" stroke={color} strokeWidth="2" fill="none" opacity="0.4" />
        </svg>
      );
    case "chat":
      return (
        <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 38 Q20 26 32 26 L66 26 Q78 26 78 38 L78 56 Q78 68 66 68 L40 68 L28 80 L32 68 Q20 68 20 56 Z" fill="white" stroke={color} strokeWidth="2" />
          <circle cx="38" cy="47" r="2.5" fill={color} />
          <circle cx="49" cy="47" r="2.5" fill={color} />
          <circle cx="60" cy="47" r="2.5" fill={color} />
          <path d="M52 62 Q52 54 60 54 L96 54 Q104 54 104 62 L104 80 Q104 88 96 88 L92 88 L98 96 L84 88 L60 88 Q52 88 52 80 Z" fill={accent} stroke={accent} strokeWidth="2" opacity="0.9" />
          <rect x="60" y="66" width="30" height="3" rx="1" fill="white" opacity="0.8" />
          <rect x="60" y="73" width="22" height="3" rx="1" fill="white" opacity="0.6" />
        </svg>
      );
    case "plan":
      return (
        <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="20" y="30" width="70" height="60" rx="2" fill="white" stroke={color} strokeWidth="2" />
          <line x1="55" y1="30" x2="55" y2="65" stroke={color} strokeWidth="2" />
          <line x1="55" y1="65" x2="90" y2="65" stroke={color} strokeWidth="2" />
          <rect x="25" y="36" width="24" height="12" rx="3" fill={accent} opacity="0.3" />
          <circle cx="72" cy="42" r="5" fill="none" stroke={color} strokeWidth="1.5" />
          <g transform="translate(78 70)">
            <path d="M0 4 L10 0 L20 4 L20 14 L10 18 L0 14 Z" fill={accent} />
            <path d="M0 4 L10 8 L20 4" stroke="white" strokeWidth="1" fill="none" opacity="0.5" />
            <path d="M10 8 L10 18" stroke="white" strokeWidth="1" opacity="0.5" />
          </g>
          <line x1="20" y1="96" x2="90" y2="96" stroke={color} strokeWidth="1.5" />
          <line x1="20" y1="94" x2="20" y2="98" stroke={color} strokeWidth="1.5" />
          <line x1="90" y1="94" x2="90" y2="98" stroke={color} strokeWidth="1.5" />
        </svg>
      );
    case "document":
      return (
        <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="28" y="20" width="56" height="78" rx="3" fill="white" stroke={color} strokeWidth="2" />
          <rect x="36" y="30" width="30" height="4" rx="1" fill={color} />
          <rect x="36" y="38" width="24" height="2.5" rx="1" fill={color} opacity="0.4" />
          {[50, 58, 66, 74].map((y, i) => (
            <g key={i}>
              <rect x="36" y={y} width="22" height="2" rx="1" fill={color} opacity="0.4" />
              <rect x="66" y={y} width="12" height="2" rx="1" fill={color} opacity="0.55" />
            </g>
          ))}
          <line x1="36" y1="82" x2="78" y2="82" stroke={color} strokeWidth="1" opacity="0.3" />
          <rect x="36" y="86" width="18" height="3" rx="1" fill={color} />
          <rect x="62" y="86" width="16" height="3" rx="1" fill={accent} />
          <g transform="translate(72 62) rotate(-12)">
            <circle cx="0" cy="0" r="16" fill="none" stroke={accent} strokeWidth="2" />
            <text x="0" y="3" textAnchor="middle" fontSize="7" fill={accent} fontFamily="system-ui" fontWeight="800">VAST</text>
          </g>
        </svg>
      );
    case "key":
      return (
        <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="22" y="30" width="50" height="64" rx="3" fill="white" stroke={color} strokeWidth="2" />
          <circle cx="47" cy="44" r="8" fill="none" stroke={color} strokeWidth="1.5" />
          <circle cx="47" cy="44" r="4" fill={accent} opacity="0.4" />
          {[[38, 60], [42, 68], [47, 62], [52, 70], [56, 60], [47, 74], [42, 80], [52, 82]].map(([x, y], i) => (
            <ellipse key={i} cx={x} cy={y} rx="1.5" ry="3" fill={accent} opacity="0.45" />
          ))}
          <line x1="22" y1="94" x2="72" y2="94" stroke={color} strokeWidth="2" />
          <g transform="translate(82 78)">
            <circle cx="0" cy="0" r="16" fill={accent} />
            <path d="M-6 0 L-2 4 L6 -4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </g>
        </svg>
      );
    default:
      return null;
  }
}

function PhotoBox({ src, n, title, height, rounded = 12 }: { src?: string; n: string; title: string; height: string | number; rounded?: number }) {
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height,
        borderRadius: rounded,
        overflow: "hidden",
        background: `linear-gradient(135deg, ${T.accentSoft} 0%, ${T.sand} 100%)`,
        border: `1px solid ${T.border}`,
      }}
    >
      {src ? (
        <NextImage
          src={src}
          alt={title}
          fill
          loading="lazy"
          sizes="(max-width: 768px) 50vw, 33vw"
          quality={78}
          style={{ objectFit: "cover" }}
        />
      ) : (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: T.muted, textAlign: "center", padding: 12 }}>
          <div>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 42, color: T.accent, opacity: 0.55, lineHeight: 1 }}>{n}</div>
            <div style={{ fontSize: 10, marginTop: 10, opacity: 0.7, letterSpacing: "0.12em", fontWeight: 700, textTransform: "uppercase", color: T.accent }}>
              Foto-placeholder
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CTA({ content, compact, onDark }: { content: StappenPlanContent; compact?: boolean; onDark?: boolean }) {
  const label = content.cta_text ?? "Vraag gratis brochure aan";
  const href = content.cta_href ?? "#brochure-form";
  return (
    <a
      href={href}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: compact ? "12px 18px" : "16px 26px",
        borderRadius: 999,
        background: T.cta,
        color: "white",
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: compact ? 14 : 15.5,
        fontWeight: 700,
        textDecoration: "none",
        letterSpacing: "-0.005em",
        boxShadow: onDark ? "0 10px 24px -8px rgba(0,0,0,0.5)" : "0 8px 20px -8px rgba(255,107,26,0.5)",
        whiteSpace: "nowrap",
      }}
    >
      {label}
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    </a>
  );
}

// ── Variant 1: horizontale timeline ────────────────────────────────────────
function Timeline({ content }: { content: StappenPlanContent }) {
  const D = DEFAULTS.timeline;
  const steps = content.steps && content.steps.length > 0 ? content.steps : DEFAULT_STEPS;
  const eyebrow = content.eyebrow ?? D.eyebrow;
  const headline = content.headline ?? D.headline;
  const subnote = content.cta_subnote ?? D.cta_subnote;
  return (
    <section
      style={{
        background: T.card,
        padding: "72px 28px",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <style>{`
        @media (max-width: 1024px) {
          .sp-timeline-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
          .sp-timeline-line { display: none; }
          .sp-timeline-item { text-align: left !important; display: flex !important; gap: 16px; align-items: flex-start; }
          .sp-timeline-circle { margin: 0 !important; }
        }
      `}</style>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          {eyebrow ? (
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", color: T.cta, marginBottom: 10, textTransform: "uppercase" }}>
              {eyebrow}
            </div>
          ) : null}
          <h2
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: "clamp(26px, 3.4vw, 42px)",
              fontWeight: 500,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: T.ink,
              margin: 0,
              textWrap: "balance",
            }}
          >
            {headline}
          </h2>
        </div>
        <div
          className="sp-timeline-grid"
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
            gap: 24,
            position: "relative",
          }}
        >
          <div
            aria-hidden
            className="sp-timeline-line"
            style={{
              position: "absolute",
              top: 30,
              left: "10%",
              right: "10%",
              height: 2,
              background: `repeating-linear-gradient(to right, ${T.border} 0 6px, transparent 6px 12px)`,
              zIndex: 0,
            }}
          />
          {steps.map((s, i) => (
            <div key={i} className="sp-timeline-item" style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
              <div
                className="sp-timeline-circle"
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 999,
                  background: T.accent,
                  color: "white",
                  display: "grid",
                  placeItems: "center",
                  margin: "0 auto 18px",
                  fontFamily: "'Fraunces', serif",
                  fontSize: 22,
                  fontWeight: 500,
                  boxShadow: "0 6px 16px -4px rgba(20,28,40,0.35)",
                  flexShrink: 0,
                }}
              >
                {s.n}
              </div>
              <div>
                {s.time ? (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "3px 9px",
                      borderRadius: 999,
                      background: T.sand,
                      color: T.accent,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      marginBottom: 8,
                      textTransform: "uppercase",
                    }}
                  >
                    <span style={{ color: T.cta }}>●</span> {s.time}
                  </div>
                ) : null}
                <h3
                  style={{
                    fontFamily: "'Fraunces', serif",
                    fontSize: 19,
                    fontWeight: 500,
                    lineHeight: 1.2,
                    letterSpacing: "-0.01em",
                    color: T.ink,
                    margin: "0 0 8px",
                  }}
                >
                  {s.title}
                </h3>
                {s.body ? (
                  <p style={{ fontSize: 13.5, lineHeight: 1.55, color: T.muted, margin: 0 }}>{s.body}</p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 56 }}>
          <CTA content={content} />
          {subnote ? (
            <div style={{ fontSize: 12, color: T.muted, marginTop: 10 }}>{subnote}</div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ── Variant 2: zigzag ─────────────────────────────────────────────────────
function Zigzag({ content }: { content: StappenPlanContent }) {
  const D = DEFAULTS.zigzag;
  const steps = content.steps && content.steps.length > 0 ? content.steps : DEFAULT_STEPS;
  const eyebrow = content.eyebrow ?? D.eyebrow;
  const headline = content.headline ?? D.headline;
  return (
    <section style={{ background: T.sand, padding: "72px 28px", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <style>{`
        @media (max-width: 1024px) {
          .sp-zig-row { grid-template-columns: auto 1fr !important; gap: 14px !important; }
          .sp-zig-row > .sp-zig-card { grid-column: 2 !important; order: 0 !important; text-align: left !important; }
          .sp-zig-row > .sp-zig-circle-wrap { grid-column: 1 !important; }
        }
      `}</style>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          {eyebrow ? (
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", color: T.cta, marginBottom: 10, textTransform: "uppercase" }}>
              {eyebrow}
            </div>
          ) : null}
          <h2
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: "clamp(24px, 3.2vw, 40px)",
              fontWeight: 500,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: T.ink,
              margin: 0,
              textWrap: "balance",
            }}
          >
            {headline}
          </h2>
        </div>
        <div style={{ display: "grid", gap: 24 }}>
          {steps.map((s, i) => {
            const right = i % 2 === 1;
            return (
              <div
                key={i}
                className="sp-zig-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 80px 1fr",
                  alignItems: "center",
                }}
              >
                <div
                  className="sp-zig-card"
                  style={{
                    textAlign: right ? "right" : "left",
                    gridColumn: right ? 3 : 1,
                    gridRow: 1,
                    order: right ? 2 : 0,
                    background: T.card,
                    padding: "22px 26px",
                    borderRadius: 14,
                    border: `1px solid ${T.border}`,
                    boxShadow: "0 2px 8px -4px rgba(0,0,0,0.05)",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      color: T.cta,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      marginBottom: 6,
                      textTransform: "uppercase",
                    }}
                  >
                    Stap {s.n}
                    {s.time ? <> · {s.time}</> : null}
                  </div>
                  <h3
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontSize: 20,
                      fontWeight: 500,
                      lineHeight: 1.2,
                      color: T.ink,
                      margin: "0 0 8px",
                    }}
                  >
                    {s.title}
                  </h3>
                  {s.body ? (
                    <p style={{ fontSize: 14, lineHeight: 1.55, color: T.muted, margin: 0 }}>{s.body}</p>
                  ) : null}
                </div>
                <div className="sp-zig-circle-wrap" style={{ gridColumn: 2, gridRow: 1, display: "grid", placeItems: "center", position: "relative" }}>
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: 999,
                      background: T.accent,
                      color: "white",
                      display: "grid",
                      placeItems: "center",
                      fontFamily: "'Fraunces', serif",
                      fontSize: 22,
                      fontWeight: 500,
                      boxShadow: "0 8px 18px -6px rgba(20,28,40,0.45)",
                      position: "relative",
                      zIndex: 2,
                    }}
                  >
                    {s.n}
                  </div>
                  {i < steps.length - 1 ? (
                    <div
                      aria-hidden
                      style={{
                        position: "absolute",
                        top: "calc(50% + 22px)",
                        height: 22,
                        width: 2,
                        background: `repeating-linear-gradient(to bottom, ${T.border} 0 4px, transparent 4px 8px)`,
                      }}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ textAlign: "center", marginTop: 48 }}>
          <CTA content={content} />
        </div>
      </div>
    </section>
  );
}

// ── Variant 3: dark display ───────────────────────────────────────────────
function Display({ content }: { content: StappenPlanContent }) {
  const D = DEFAULTS.display;
  const steps = content.steps && content.steps.length > 0 ? content.steps : DEFAULT_STEPS;
  const eyebrow = content.eyebrow ?? D.eyebrow;
  const customHeadline = content.headline;
  const phone = content.phone;
  return (
    <section
      style={{
        background: T.ink,
        padding: "80px 28px",
        fontFamily: "system-ui, -apple-system, sans-serif",
        color: "white",
      }}
    >
      <style>{`
        @media (max-width: 700px) {
          .sp-display-row { grid-template-columns: 80px 1fr !important; gap: 12px !important; }
          .sp-display-num { font-size: 54px !important; }
          .sp-display-time { display: none !important; }
        }
      `}</style>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <div style={{ marginBottom: 56, maxWidth: 900 }}>
          {eyebrow ? (
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", color: T.cta, marginBottom: 12, textTransform: "uppercase" }}>
              {eyebrow}
            </div>
          ) : null}
          <h2
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: "clamp(30px, 4.6vw, 56px)",
              fontWeight: 400,
              lineHeight: 1.05,
              letterSpacing: "-0.025em",
              color: "white",
              margin: 0,
            }}
          >
            {customHeadline ? (
              customHeadline
            ) : (
              <>
                Vijf stappen.<br />
                <span style={{ fontStyle: "italic", color: T.cta }}>Eén</span> resultaat.
              </>
            )}
          </h2>
        </div>
        <div style={{ display: "grid", gap: 4 }}>
          {steps.map((s, i) => (
            <div
              key={i}
              className="sp-display-row"
              style={{
                display: "grid",
                gridTemplateColumns: "180px 1fr auto",
                gap: 32,
                alignItems: "baseline",
                padding: "26px 0",
                borderTop: i === 0 ? "1px solid rgba(255,255,255,0.15)" : "none",
                borderBottom: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <div
                className="sp-display-num"
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: 88,
                  fontWeight: 300,
                  lineHeight: 1,
                  letterSpacing: "-0.03em",
                  color: T.cta,
                }}
              >
                {s.n}
              </div>
              <div>
                <h3
                  style={{
                    fontFamily: "'Fraunces', serif",
                    fontSize: "clamp(18px, 2vw, 26px)",
                    fontWeight: 500,
                    lineHeight: 1.15,
                    letterSpacing: "-0.015em",
                    color: "white",
                    margin: "0 0 6px",
                  }}
                >
                  {s.title}
                </h3>
                {s.body ? (
                  <p style={{ fontSize: 14.5, lineHeight: 1.55, color: "rgba(255,255,255,0.65)", margin: 0, maxWidth: 520 }}>
                    {s.body}
                  </p>
                ) : null}
              </div>
              {s.time ? (
                <div
                  className="sp-display-time"
                  style={{
                    alignSelf: "center",
                    padding: "6px 14px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.2)",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    color: "rgba(255,255,255,0.8)",
                    whiteSpace: "nowrap",
                    textTransform: "uppercase",
                  }}
                >
                  {s.time}
                </div>
              ) : null}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 48, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
          <CTA content={content} onDark />
          {phone ? (
            <a
              href={`tel:${phone.replace(/\s/g, "")}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "16px 22px",
                color: "white",
                fontSize: 15,
                fontWeight: 600,
                textDecoration: "none",
                opacity: 0.85,
                borderBottom: "1px solid rgba(255,255,255,0.3)",
              }}
            >
              Of bel direct → {phone}
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ── Variant 4: accordion ──────────────────────────────────────────────────
function Accordion({ content }: { content: StappenPlanContent }) {
  const D = DEFAULTS.accordion;
  const steps = content.steps && content.steps.length > 0 ? content.steps : DEFAULT_STEPS;
  const eyebrow = content.eyebrow ?? D.eyebrow;
  const customHeadline = content.headline;
  const body = content.body ?? D.body;
  const phone = content.phone;
  const [open, setOpen] = useState<number>(0);

  return (
    <section style={{ background: T.bg, padding: "72px 28px", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <style>{`
        @media (max-width: 1024px) {
          .sp-acc-layout { grid-template-columns: 1fr !important; gap: 28px !important; }
          .sp-acc-left { position: relative !important; top: auto !important; }
          .sp-acc-row { padding: 16px 18px !important; }
          .sp-acc-time { display: none !important; }
        }
      `}</style>
      <div
        className="sp-acc-layout"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1.3fr",
          gap: 64,
          alignItems: "start",
        }}
      >
        <div className="sp-acc-left" style={{ position: "sticky", top: 72 }}>
          {eyebrow ? (
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", color: T.cta, marginBottom: 12, textTransform: "uppercase" }}>
              {eyebrow}
            </div>
          ) : null}
          <h2
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: "clamp(26px, 3.4vw, 42px)",
              fontWeight: 500,
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              color: T.ink,
              margin: "0 0 18px",
              textWrap: "balance",
            }}
          >
            {customHeadline ? (
              customHeadline
            ) : (
              <>
                Geen stress,
                <br />
                <span style={{ fontStyle: "italic" }}>geen</span> verrassingen.
              </>
            )}
          </h2>
          {body ? (
            <p style={{ fontSize: 15.5, lineHeight: 1.6, color: T.muted, margin: "0 0 28px", maxWidth: 360 }}>{body}</p>
          ) : null}
          <CTA content={content} />
          {phone ? (
            <div style={{ fontSize: 12, color: T.muted, marginTop: 10 }}>
              Of bel <strong style={{ color: T.ink }}>{phone}</strong>
            </div>
          ) : null}
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {steps.map((s, i) => {
            const isOpen = open === i;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="sp-acc-row"
                style={{
                  textAlign: "left",
                  background: isOpen ? T.card : "transparent",
                  border: `1px solid ${isOpen ? T.cta : T.border}`,
                  borderRadius: 14,
                  padding: "22px 26px",
                  cursor: "pointer",
                  transition: "all 0.25s",
                  boxShadow: isOpen ? "0 8px 24px -12px rgba(0,0,0,0.1)" : "none",
                  fontFamily: "inherit",
                  width: "100%",
                }}
                aria-expanded={isOpen}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 999,
                      background: isOpen ? T.cta : T.accentSoft,
                      color: isOpen ? "white" : T.accent,
                      display: "grid",
                      placeItems: "center",
                      fontFamily: "'Fraunces', serif",
                      fontSize: 17,
                      fontWeight: 500,
                      flexShrink: 0,
                      transition: "all 0.25s",
                    }}
                  >
                    {s.n}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3
                      style={{
                        fontFamily: "'Fraunces', serif",
                        fontSize: 19,
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: T.ink,
                        margin: 0,
                      }}
                    >
                      {s.title}
                    </h3>
                  </div>
                  {s.time ? (
                    <div
                      className="sp-acc-time"
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        color: isOpen ? T.cta : T.muted,
                        padding: "4px 10px",
                        borderRadius: 999,
                        background: isOpen ? T.sand : "transparent",
                        border: isOpen ? "none" : `1px solid ${T.border}`,
                        whiteSpace: "nowrap",
                        textTransform: "uppercase",
                        flexShrink: 0,
                      }}
                    >
                      {s.time}
                    </div>
                  ) : null}
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      color: T.muted,
                      transform: isOpen ? "rotate(180deg)" : "rotate(0)",
                      transition: "transform 0.25s",
                      flexShrink: 0,
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {isOpen && s.body ? (
                  <p style={{ fontSize: 14.5, lineHeight: 1.6, color: T.muted, margin: "14px 0 0 60px" }}>{s.body}</p>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Variant 5: foto-kaarten met verticaal label ──────────────────────────
function PhotoCards({ content }: { content: StappenPlanContent }) {
  const D = DEFAULTS["photo-cards"];
  const steps = content.steps && content.steps.length > 0 ? content.steps : DEFAULT_VISUAL_STEPS;
  return (
    <section style={{ background: T.card, padding: "72px 28px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 1024px){ .sp-cards-grid { grid-template-columns: 1fr !important; } .sp-cards-label { display: none !important; } }`}</style>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          {(content.eyebrow ?? D.eyebrow) ? (
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", color: T.cta, marginBottom: 10, textTransform: "uppercase" }}>
              {content.eyebrow ?? D.eyebrow}
            </div>
          ) : null}
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(24px, 3vw, 40px)", fontWeight: 500, lineHeight: 1.1, letterSpacing: "-0.02em", color: T.ink, margin: 0, textWrap: "balance" }}>
            {content.headline ?? D.headline}
          </h2>
        </div>
        <div className="sp-cards-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${steps.length}, 1fr)`, gap: 20 }}>
          {steps.map((s, i) => (
            <div key={i}>
              <div style={{ position: "relative", display: "flex", alignItems: "stretch", marginBottom: 18 }}>
                <div style={{ flex: 1, position: "relative" }}>
                  <PhotoBox {...(s.photo_src ? { src: s.photo_src } : {})} n={s.n} title={s.title} height={260} rounded={10} />
                  <div style={{ position: "absolute", top: 12, left: 12, background: T.accent, color: "white", padding: "5px 10px", borderRadius: 6, fontFamily: "'Fraunces', serif", fontSize: 13, fontWeight: 500 }}>{s.n}</div>
                </div>
                <div className="sp-cards-label" style={{ width: 28, background: T.accent, opacity: 0.85, display: "grid", placeItems: "center", borderRadius: "0 10px 10px 0" }}>
                  <div style={{ transform: "rotate(90deg)", whiteSpace: "nowrap", fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", color: "white", textTransform: "uppercase" }}>
                    {s.title.split(" ").slice(0, 2).join(" ")}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "center", padding: "0 6px" }}>
                <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500, color: T.ink, lineHeight: 1.2, marginBottom: 8 }}>
                  <span style={{ color: T.cta, fontWeight: 400 }}>{s.n} / </span>
                  {s.title}
                </div>
                {s.body ? <p style={{ fontSize: 13, lineHeight: 1.55, color: T.muted, margin: 0 }}>{s.body}</p> : null}
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 48 }}><CTA content={content} /></div>
      </div>
    </section>
  );
}

// ── Variant 6: split — grote foto links, interactieve lijst rechts ───────
function PhotoSplit({ content }: { content: StappenPlanContent }) {
  const D = DEFAULTS["photo-split"];
  const steps = content.steps && content.steps.length > 0 ? content.steps : DEFAULT_VISUAL_STEPS;
  const [active, setActive] = useState(0);
  const s = steps[active] ?? steps[0]!;
  return (
    <section style={{ background: T.bg, padding: "64px 28px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`@media (max-width: 1024px){ .sp-split-grid { grid-template-columns: 1fr !important; gap: 24px !important; } }`}</style>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ marginBottom: 40, maxWidth: 700 }}>
          {(content.eyebrow ?? D.eyebrow) ? (
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", color: T.cta, marginBottom: 10, textTransform: "uppercase" }}>
              {content.eyebrow ?? D.eyebrow}
            </div>
          ) : null}
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(24px, 3vw, 40px)", fontWeight: 500, lineHeight: 1.1, letterSpacing: "-0.02em", color: T.ink, margin: 0, textWrap: "balance" }}>
            {content.headline ?? D.headline}
          </h2>
        </div>
        <div className="sp-split-grid" style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 48, alignItems: "stretch" }}>
          <div style={{ position: "relative" }}>
            <PhotoBox {...(s.photo_src ? { src: s.photo_src } : {})} n={s.n} title={s.title} height={480} rounded={14} />
            <div style={{ position: "absolute", bottom: 18, left: 18, display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)", color: "white", padding: "8px 14px", borderRadius: 8 }}>
              <span style={{ fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 500, color: T.cta }}>{s.n}</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{s.title}</span>
            </div>
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {steps.map((step, i) => {
              const isActive = active === i;
              return (
                <button
                  key={i}
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => setActive(i)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "48px 1fr auto",
                    gap: 16,
                    alignItems: "center",
                    padding: "18px 20px",
                    background: isActive ? T.card : "transparent",
                    border: `1px solid ${isActive ? T.cta : T.border}`,
                    borderRadius: 12,
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    fontFamily: "inherit",
                    boxShadow: isActive ? "0 8px 20px -10px rgba(0,0,0,0.12)" : "none",
                  }}
                >
                  <div style={{ width: 44, height: 44, borderRadius: 999, background: isActive ? T.cta : T.accentSoft, color: isActive ? "white" : T.accent, display: "grid", placeItems: "center", fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 500, transition: "all 0.2s" }}>
                    {step.n}
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Fraunces', serif", fontSize: 17, fontWeight: 500, color: T.ink, marginBottom: 3 }}>{step.title}</div>
                    {isActive && step.body ? (
                      <div style={{ fontSize: 12.5, lineHeight: 1.5, color: T.muted, marginTop: 4 }}>{step.body}</div>
                    ) : null}
                  </div>
                  {step.time ? (
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: isActive ? T.cta : T.muted, textTransform: "uppercase", whiteSpace: "nowrap" }}>
                      {step.time}
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ marginTop: 40 }}><CTA content={content} /></div>
      </div>
    </section>
  );
}

// ── Variant 7: ronde illustratie-cirkels ──────────────────────────────────
function IconCircles({ content }: { content: StappenPlanContent }) {
  const D = DEFAULTS["icon-circles"];
  const steps = content.steps && content.steps.length > 0 ? content.steps : DEFAULT_VISUAL_STEPS;
  return (
    <section style={{ background: T.sand, padding: "80px 28px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`
        @media (max-width: 1024px){
          .sp-circ-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
          .sp-circ-item {
            display: grid !important;
            grid-template-columns: 92px 1fr !important;
            gap: 18px !important;
            align-items: start !important;
            text-align: left !important;
          }
          .sp-circ-arc { display: none !important; }
          /* Verklein de cirkel zodat hij netjes in de linkerkolom past */
          .sp-circ-item > div:first-child {
            width: 88px !important;
            height: 88px !important;
            margin: 0 !important;
            flex-shrink: 0 !important;
          }
          .sp-circ-item > div:first-child > div:last-child {
            width: 26px !important;
            height: 26px !important;
            font-size: 11px !important;
            top: -6px !important;
            right: -6px !important;
          }
          .sp-circ-item h3 { font-size: 16px !important; margin-top: 2px !important; }
          .sp-circ-item p { font-size: 13px !important; }
        }
      `}</style>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          {(content.eyebrow ?? D.eyebrow) ? (
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", color: T.cta, marginBottom: 10, textTransform: "uppercase" }}>
              {content.eyebrow ?? D.eyebrow}
            </div>
          ) : null}
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(26px, 3.6vw, 44px)", fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em", color: T.ink, margin: 0, textWrap: "balance" }}>
            {content.headline ?? D.headline}
          </h2>
        </div>
        <div className="sp-circ-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${steps.length}, 1fr)`, gap: 24, position: "relative" }}>
          <svg
            className="sp-circ-arc"
            aria-hidden
            style={{ position: "absolute", top: 45, left: "10%", right: "10%", width: "80%", height: 40, pointerEvents: "none" }}
            viewBox="0 0 800 40"
            preserveAspectRatio="none"
          >
            <path d="M 20 20 Q 200 -20 400 20 T 780 20" stroke={T.cta} strokeWidth="1.5" strokeDasharray="3 5" fill="none" opacity="0.5" />
          </svg>
          {steps.map((s, i) => (
            <div key={i} className="sp-circ-item" style={{ textAlign: "center" }}>
              <div style={{ width: 150, height: 150, borderRadius: 999, background: T.card, border: `2px solid ${T.accent}`, margin: "0 auto 22px", position: "relative", display: "grid", placeItems: "center", boxShadow: "0 12px 30px -12px rgba(0,0,0,0.12)" }}>
                <div style={{ width: "65%", height: "65%" }}>
                  <Illustration name={s.illustration ?? "brochure"} color={T.accent} accent={T.cta} />
                </div>
                <div style={{ position: "absolute", top: -8, right: -8, width: 38, height: 38, borderRadius: 999, background: T.cta, color: "white", display: "grid", placeItems: "center", fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 500, border: `3px solid ${T.sand}` }}>
                  {s.n}
                </div>
              </div>
              <div>
                <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500, lineHeight: 1.2, color: T.ink, margin: "0 0 6px" }}>{s.title}</h3>
                {s.body ? <p style={{ fontSize: 13, lineHeight: 1.55, color: T.muted, margin: 0 }}>{s.body}</p> : null}
                {s.time ? (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 10, padding: "3px 9px", borderRadius: 999, background: T.card, border: `1px solid ${T.border}`, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: T.accent, textTransform: "uppercase" }}>
                    <span style={{ color: T.cta }}>●</span> {s.time}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 56 }}><CTA content={content} /></div>
      </div>
    </section>
  );
}

// ── Variant 8: foto-zigzag met illustratie-overlay ────────────────────────
function PhotoZigzag({ content }: { content: StappenPlanContent }) {
  const D = DEFAULTS["photo-zigzag"];
  const steps = content.steps && content.steps.length > 0 ? content.steps : DEFAULT_VISUAL_STEPS;
  return (
    <section style={{ background: T.card, padding: "72px 28px", fontFamily: "system-ui, sans-serif" }}>
      <style>{`
        @media (max-width: 1024px){
          .sp-zz-wrap > div:nth-child(2) > div { gap: 32px !important; }
          .sp-zz-row { grid-template-columns: 1fr !important; gap: 14px !important; padding-bottom: 14px; border-bottom: 1px solid rgba(20,28,40,0.06); }
          .sp-zz-row > :first-child { order: 0 !important; }
          .sp-zz-row .sp-zz-photo { height: 200px !important; }
          .sp-zz-row .sp-zz-illu { width: 52px !important; height: 52px !important; right: auto !important; left: 12px !important; bottom: -12px !important; padding: 8px !important; }
          .sp-zz-row h3 { font-size: 20px !important; }
          .sp-zz-row p { font-size: 14px !important; }
        }
      `}</style>
      <div className="sp-zz-wrap" style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          {(content.eyebrow ?? D.eyebrow) ? (
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", color: T.cta, marginBottom: 10, textTransform: "uppercase" }}>
              {content.eyebrow ?? D.eyebrow}
            </div>
          ) : null}
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(26px, 3.4vw, 42px)", fontWeight: 500, lineHeight: 1.1, letterSpacing: "-0.02em", color: T.ink, margin: 0, textWrap: "balance" }}>
            {content.headline ?? D.headline}
          </h2>
        </div>
        <div style={{ display: "grid", gap: 44 }}>
          {steps.map((s, i) => {
            const flip = i % 2 === 1;
            return (
              <div key={i} className="sp-zz-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" }}>
                <div style={{ order: flip ? 2 : 0, position: "relative" }}>
                  <div className="sp-zz-photo" style={{ width: "100%" }}>
                    <PhotoBox {...(s.photo_src ? { src: s.photo_src } : {})} n={s.n} title={s.title} height={300} rounded={14} />
                  </div>
                  <div className="sp-zz-illu" style={{ position: "absolute", bottom: -16, right: flip ? "auto" : -16, left: flip ? -16 : "auto", width: 74, height: 74, borderRadius: 12, background: T.card, boxShadow: "0 8px 24px -6px rgba(0,0,0,0.15)", display: "grid", placeItems: "center", padding: 10 }}>
                    <Illustration name={s.illustration ?? "brochure"} color={T.accent} accent={T.cta} />
                  </div>
                </div>
                <div>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 999, background: T.cta, color: "white", display: "grid", placeItems: "center", fontFamily: "'Fraunces', serif", fontSize: 14, fontWeight: 500 }}>{s.n}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: T.muted, textTransform: "uppercase" }}>
                      STAP {s.n.replace(/^0/, "")}{s.time ? ` · ${s.time}` : ""}
                    </div>
                  </div>
                  <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(20px, 2.4vw, 28px)", fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.015em", color: T.ink, margin: "0 0 12px" }}>
                    {s.title}
                  </h3>
                  {s.body ? <p style={{ fontSize: 15, lineHeight: 1.6, color: T.muted, margin: 0 }}>{s.body}</p> : null}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ textAlign: "center", marginTop: 56 }}><CTA content={content} /></div>
      </div>
    </section>
  );
}

export default function StappenPlanBlock({ content, variant = "timeline" }: Props) {
  switch (variant) {
    case "zigzag":
      return <Zigzag content={content} />;
    case "display":
      return <Display content={content} />;
    case "accordion":
      return <Accordion content={content} />;
    case "photo-cards":
      return <PhotoCards content={content} />;
    case "photo-split":
      return <PhotoSplit content={content} />;
    case "icon-circles":
      return <IconCircles content={content} />;
    case "photo-zigzag":
      return <PhotoZigzag content={content} />;
    case "timeline":
    default:
      return <Timeline content={content} />;
  }
}
